import { useRef } from 'react';
// Components
import { showToast } from 'components/common/Toast/Toast';

export const PARTLY_SAVED_MESSAGE =
  'Part of the changes were saved before the error. The form now shows the saved event.';

// A picked poster file only serialises by name, size and date.
const serializeFormValues = (values: unknown): string =>
  JSON.stringify(values, (_key, value) =>
    value instanceof File ? [value.name, value.size, value.lastModified] : value
  );

type PartlySavedForm<Values> = {
  /** Remembers the values the steps that went through were saved with. */
  markSaved: (values: Values) => void;
  /** True once a step went through and the form no longer matches it. */
  hasChangedSinceSave: (values: Values) => boolean;
  /** Puts the form back to the saved values and tells the host. */
  restoreSaved: () => void;
};

/**
 * Keeps a multi-step save from building its remaining steps on other values than the steps already saved, so a
 * retry neither repeats a saved step nor silently drops an edit.
 */
export const usePartlySavedForm = <Values>(
  reset: (values: Values) => void
): PartlySavedForm<Values> => {
  const savedRef = useRef<Values | null>(null);

  return {
    markSaved: (values) => {
      savedRef.current = values;
    },
    hasChangedSinceSave: (values) =>
      savedRef.current !== null &&
      serializeFormValues(values) !== serializeFormValues(savedRef.current),
    restoreSaved: () => {
      if (!savedRef.current) {
        return;
      }

      reset(savedRef.current);
      showToast({ message: PARTLY_SAVED_MESSAGE, type: 'warning' });
    },
  };
};
